import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AdminTheme from '../AdminTheme';
import { fetchPayload, saveStep } from './api';
import PreviewPanel from './PreviewPanel';
import STEPS from './steps';

/**
 * Guided setup for a shop product.
 *
 * Two rules this holds to:
 *
 *  1. Every write goes to PATCH .../wizard, the same endpoint the ERB forms
 *     posted, so the server runs the identical apply_* method the one-page editor
 *     uses. This app is a nicer way to drive those writers, not a second set.
 *  2. A save answers with the WHOLE refreshed payload. Setting a pricelist
 *     changes the packages, the styles, the checklist and the preview at once,
 *     so the client never tries to work out what a save implied.
 *
 * No <a href> that also handles a click anywhere in here: CLAUDE.md records the
 * incident where exactly that race-conditioned with rack-mini-profiler.
 */
const AUTOSAVE_MS = 700;

// Below this the steps and the preview stack. It is the sum of what each column
// needs to be worth showing (about 620px each) plus the gap and the page's own
// padding -- not a device breakpoint, and deliberately wider than MUI's `lg`
// (1200), which would have kept a 1280px window in the cramped two-column layout.
const SIDE_BY_SIDE_AT = 1360;

const STATE_LABEL = { live: 'Live', attention: 'On — needs fixing', ready: 'Ready to publish' };
const STATE_COLOR = { live: 'success', attention: 'error', ready: 'info' };

const WizardBody = ({ productId, initialStep }) => {
  const [payload, setPayload] = useState(null);
  const [step, setStep] = useState(initialStep);
  const [schoolId, setSchoolId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  // Collapsed by default: the state and the count answer "can I publish this yet",
  // and the eight labels are only wanted once the answer is no. Keeping it shut also
  // hands the height back to the preview, which is the other half of this column.
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [errors, setErrors] = useState([]);
  const [fatal, setFatal] = useState(null);
  // Unsaved edits whose preview effect is unambiguous (card image, styles).
  const [overrides, setOverrides] = useState({});
  const timer = useRef(null);
  // What the pending autosave will send, accumulated across calls (see `autosave`).
  const pending = useRef({});

  const load = useCallback(async (nextStep, nextSchoolId) => {
    setBusy(true);
    try {
      const data = await fetchPayload(productId, nextStep, nextSchoolId);
      setPayload(data);
      setOverrides({});
      setFatal(null);
    } catch (e) {
      setFatal(e.message);
    } finally {
      setBusy(false);
    }
  }, [productId]);

  useEffect(() => { load(step, schoolId); }, [load, step, schoolId]);

  // Cancel a pending autosave when the step changes, so a debounced edit cannot
  // land against the step you have just left. The accumulated payload goes with it:
  // `commit` sends to whatever step is current, so a leftover would be posted to the
  // wrong writer.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    pending.current = {};
  }, [step]);

  const commit = useCallback(async (fields, { advance = false } = {}) => {
    setBusy(true);
    setErrors([]);
    try {
      const data = await saveStep(productId, step, fields, schoolId);
      setPayload(data);
      setOverrides({});
      if (data.ok) {
        setSavedAt(Date.now());
        if (advance && data.next_step) setStep(data.next_step);
      } else {
        setErrors(data.errors || ['That could not be saved.']);
      }
      return data.ok;
    } catch (e) {
      setFatal(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [productId, step, schoolId]);

  // Debounced: for text and number fields, where saving every keystroke is noise.
  //
  // Payloads MERGE across calls. With one timer and one payload, an edit to any
  // field cancelled a pending edit to a different one and only the last survived:
  // type a name, click into the tagline within the window, and the name was
  // silently dropped -- still shown in its (uncontrolled) input, so nothing looked
  // wrong. The same collision hit two variants' prices edited back to back.
  const autosave = useCallback((fields) => {
    // One level deep: `shop_product` and `prices` are bags of independent fields, so
    // edits to different keys must both survive. A scalar (`shipping`) is replaced --
    // for a single field the newest value is the right one.
    pending.current = Object.entries(fields).reduce((acc, [key, value]) => {
      const prev = acc[key];
      const bothObjects = [prev, value].every(
        (x) => x && typeof x === 'object' && !Array.isArray(x),
      );

      return { ...acc, [key]: bothObjects ? { ...prev, ...value } : value };
    }, pending.current);

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const merged = pending.current;
      pending.current = {};
      commit(merged);
    }, AUTOSAVE_MS);
  }, [commit]);

  if (fatal) {
    return (
      <Alert
        severity="error"
        action={<Button color="inherit" onClick={() => load(step, schoolId)}>Try again</Button>}
      >
        {fatal}
      </Alert>
    );
  }

  if (!payload) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      </Stack>
    );
  }

  const {
    steps, readiness, choices, preview, product, blockers,
  } = payload;
  const index = steps.findIndex((x) => x.key === step);
  const current = steps[index] || steps[0];
  const StepBody = STEPS[current.key];
  const blocker = blockers[step];
  const goTo = (key) => { if (key !== step) setStep(key); };
  const doneCount = readiness.checklist.filter((i) => i.done).length;
  // What to name in the collapsed header. The first unmet item is the one to work on,
  // and the checklist is already in dependency order (Shop::Product#readiness_checklist).
  const firstMissing = readiness.checklist.find((i) => !i.done);

  return (
    <Box>
      {/* A stepper, not pills. Pills said nothing about order or progress and the
          filled one read as a button to press; setup IS a sequence, and this shows
          where you are in it, what is behind you, and what is not workable yet.
          This used to be an `overflowX: 'auto'` wrapper, "scrolls rather than
          squeezing". It did neither: an alternativeLabel Stepper sizes its labels to
          the container and wraps them, so scrollWidth never exceeded clientWidth and
          there was nothing to scroll sideways to. What it DID do was draw a vertical
          scrollbar across the rail -- overflow-x: auto computes overflow-y: auto, and
          MUI gives StepButton `margin: -24px 0` in this variant, so the button's box
          spills 24px past the row it is laid out in -- so the padding matches that
          exactly, and the button's hit area sits inside the rail instead of over its
          neighbours. */}
      <Box sx={{ mb: 2, py: 3 }}>
        <Stepper
          nonLinear
          alternativeLabel
          activeStep={index}
          // px absorbs the few pixels an alternativeLabel connector spills past the
          // first and last step, which otherwise put a scrollbar under a stepper
          // that fits perfectly well.
          sx={{ minWidth: steps.length * 150, px: 1 }}
        >
          {steps.map((x, i) => {
            const blocked = blockers[x.key];
            return (
              <Step key={x.key} completed={!blocked && i < index}>
                <StepButton
                  onClick={() => goTo(x.key)}
                  title={blocked ? blocked.message : undefined}
                  optional={blocked ? (
                    <Typography variant="caption" color="warning.main">
                      needs an earlier step
                    </Typography>
                  ) : undefined}
                >
                  {x.title}
                </StepButton>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          // Stacked by default; side by side only once both columns can be usable.
          // Squeezing them into 1280px gave a 600px step column with wrapping table
          // headers and a preview too narrow to show a package card as a customer
          // would see it -- so the page technically fitted and told you nothing.
          flexDirection: 'column',
          [`@media (min-width:${SIDE_BY_SIDE_AT}px)`]: { flexDirection: 'row' },
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            width: '100%',
            [`@media (min-width:${SIDE_BY_SIDE_AT}px)`]: {
              flex: '1 1 620px', width: 'auto', maxWidth: 1180,
            },
          }}
        >
          <Paper>
            {busy ? <LinearProgress /> : null}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}
            >
              <Typography variant="subtitle2">
                {`Step ${index + 1} of ${steps.length} · ${current.title}`}
              </Typography>
              {!busy && savedAt
                ? <Typography variant="caption" color="success.main">saved</Typography>
                : null}
            </Stack>
            <Divider />
            <Box sx={{ p: 2 }}>
              {errors.length ? <Alert severity="error" sx={{ mb: 2 }}>{errors.join('. ')}</Alert> : null}

              {/* What a copy could not carry over. On the page rather than in a
                  flash: a real copy yields about a dozen of these, which overflowed
                  the session cookie, and they describe work still to do — so they
                  stay until dismissed. */}
              {product.setup_notes && product.setup_notes.length ? (
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                  action={(
                    <Button
                      color="inherit"
                      disabled={busy}
                      onClick={() => commit({ dismiss_setup_notes: '1' })}
                    >
                      Got it
                    </Button>
                  )}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {`Copied from an existing product — ${product.setup_notes.length} `
                      + `${product.setup_notes.length === 1 ? 'thing' : 'things'} to check`}
                  </Typography>
                  {/* Scrolls rather than growing: a real copy yields around twenty
                      notes, which pushed the step's own form off the screen. */}
                  <Box
                    component="ul"
                    sx={{
                      m: 0, pl: 2.5, maxHeight: 190, overflowY: 'auto',
                    }}
                  >
                    {product.setup_notes.map((note) => (
                      <Typography key={note} component="li" variant="body2">{note}</Typography>
                    ))}
                  </Box>
                </Alert>
              ) : null}

              {blocker ? (
                <Alert severity="warning">
                  <Typography variant="body2" sx={{ mb: 1 }}>{blocker.message}</Typography>
                  <Button variant="contained" onClick={() => goTo(blocker.step)}>
                    Go to that step
                  </Button>
                  {steps[index + 1] ? (
                    <Button sx={{ ml: 1 }} onClick={() => goTo(steps[index + 1].key)}>
                      Skip for now
                    </Button>
                  ) : null}
                </Alert>
              ) : null}

              {!blocker && StepBody ? (
                <StepBody
                  product={product}
                  choices={choices}
                  readiness={readiness}
                  steps={steps}
                  preview={preview}
                  busy={busy}
                  commit={commit}
                  autosave={autosave}
                  reload={() => load(step, schoolId)}
                  setOverrides={setOverrides}
                  goTo={goTo}
                />
              ) : null}

              {!blocker && !StepBody ? (
                <Typography variant="body2" color="text.secondary">
                  Nothing to set on this step.
                </Typography>
              ) : null}
            </Box>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Changes save as you make them — there is no separate save button.
          </Typography>
        </Box>

        {/* Sticky is only useful if it FITS: capped to the viewport and scrolling
            inside, or a preview taller than the window just scrolls away. */}
        <Box
          sx={{
            minWidth: 0,
            width: '100%',
            // Sticky only alongside the steps. Stacked, a pinned pane capped to the
            // viewport would scroll its own content while the page sat still.
            [`@media (min-width:${SIDE_BY_SIDE_AT}px)`]: {
              flex: '1 1 620px',
              width: 'auto',
              maxWidth: 760,
              position: 'sticky',
              top: 8,
              alignSelf: 'flex-start',
              maxHeight: 'calc(100vh - 16px)',
              // NOT overflowY: auto. This column used to scroll AND the preview
              // inside it scrolled again, so the pane had two nested bars of its own
              // on top of the page's. It is a flex column instead, and the preview's
              // own stage takes whatever height is left (minHeight: 0 lets it shrink
              // below its content, which is what makes the chain work) -- one
              // scroller, and it is the one the storefront components are built for:
              // their sticky banner and fixed cart bar pin to it.
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            },
          }}
        >
          {/* Above the preview, and flexShrink: 0 so it stays whole when open. In a
              flex column capped to the viewport the default (shrink to fit) clipped
              it mid-item, which reads as "the checklist has three things in it". */}
          <Paper sx={{ flexShrink: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setChecklistOpen((open) => !open)}
              sx={{
                px: 2, py: 1.25, bgcolor: 'action.hover', cursor: 'pointer',
              }}
              role="button"
              aria-expanded={checklistOpen}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setChecklistOpen((open) => !open);
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">
                  {STATE_LABEL[readiness.state] || 'Draft'}
                </Typography>
                <Chip
                  label={`${doneCount}/${readiness.checklist.length}`}
                  color={STATE_COLOR[readiness.state] || 'warning'}
                />
                {/* Collapsed, the count alone does not say what is missing -- so the
                    first unmet item comes up to the header rather than being hidden
                    behind a click. */}
                {!checklistOpen && firstMissing ? (
                  <Typography variant="caption" color="warning.main" noWrap>
                    {`next: ${firstMissing.label}`}
                  </Typography>
                ) : null}
              </Stack>
              {checklistOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Stack>
            <Collapse in={checklistOpen} unmountOnExit>
              <Divider />
              <List dense disablePadding sx={{ py: 0.5 }}>
                {readiness.checklist.map((item) => (
                  <ListItem key={item.key} sx={{ py: 0.15 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      {item.done
                        ? <CheckCircleIcon fontSize="small" color="success" />
                        : <RadioButtonUncheckedIcon fontSize="small" color="warning" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: item.done ? 'text.primary' : 'warning.main',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Paper>

          <PreviewPanel
            preview={preview}
            schools={product.schools}
            schoolId={schoolId}
            onSchoolChange={setSchoolId}
            overrides={overrides}
            styles={choices.styles}
            busy={busy}
          />
        </Box>
      </Box>
    </Box>
  );
};

WizardBody.propTypes = {
  productId: PropTypes.number.isRequired,
  initialStep: PropTypes.string,
};
WizardBody.defaultProps = { initialStep: 'basics' };

const ShopProductWizard = (props) => (
  <AdminTheme>
    <WizardBody {...props} />
  </AdminTheme>
);

ShopProductWizard.propTypes = WizardBody.propTypes;
ShopProductWizard.defaultProps = WizardBody.defaultProps;

export default ShopProductWizard;
